/**
 * API Interceptors
 * 
 * Request and response interceptors for the API client
 * Handles authentication, logging, and error handling
 */

import apiClient from './config';
import CryptoJS from 'crypto-js';
import config from '../../config/environment';
import authStorage from '../../utils/authStorage';
import { isTimeoutError } from '../../utils/apiErrorHandler';

/**
 * Request Interceptor
 * Adds authentication token and logs requests
 */
apiClient.interceptors.request.use(
  (requestConfig) => {
    // Mitigate "Insecure HTTP Methods" vulnerability (CWE-749)
    // Convert PUT, PATCH, DELETE to POST with _method override
    // This allows disabling these verbs at the server/WAF level
    const method = requestConfig.method?.toLowerCase();
    if (['put', 'patch', 'delete'].includes(method)) {
      requestConfig.method = 'post';

      if (requestConfig.data instanceof FormData) {
        requestConfig.data.append('_method', method.toUpperCase());
      } else {
        requestConfig.data = {
          ...requestConfig.data || {},
          _method: method.toUpperCase(),
        };
      }
    }

    // Add authentication token if available (read from cookies)
    const token = authStorage.getItem('auth_token');
    if (token) {
      // Always add "Bearer " prefix to the token for Laravel Sanctum
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    // Ensure Accept header is set for Laravel API
    requestConfig.headers.Accept = 'application/json';

    // Add request timestamp
    requestConfig.metadata = { startTime: new Date() };

    // Log request details in development
    if (config.api.enableLogging) {
      console.log('API Request:', {
        method: requestConfig.method?.toUpperCase(),
        url: requestConfig.url,
        headers: requestConfig.headers,
        data: requestConfig.data,
      });
    }

    return requestConfig;
  },
  (error) => {
    if (config.api.enableLogging) {
      console.error('API Request Error:', error);
    }
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handles successful responses and errors globally
 */
apiClient.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = new Date() - response.config.metadata.startTime;

    // Log response in development
    if (config.api.enableLogging) {
      console.log('API Response:', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        duration: `${duration}ms`,
        data: response.data,
      });
    }

    // ---- Response Integrity Verification ----
    const integrityHeader = response.headers['x-response-integrity'];
    if (integrityHeader) {
      try {
        const appKey = process.env.REACT_APP_RESPONSE_INTEGRITY_KEY;
        if (!appKey || appKey.includes('YOUR_BACKEND_APP_KEY_HERE')) {
          // Key not configured - skip verification but log warning
          if (config.api.enableLogging) {
            console.warn('Response integrity key not configured; skipping verification for:', response.config.url);
          }
        } else {
          // Helper to sort object keys for deterministic JSON stringification
          const sortObject = (obj) => {
            if (Array.isArray(obj)) return obj.map(sortObject);
            if (obj && typeof obj === 'object') {
              return Object.keys(obj)
                .sort()
                .reduce((acc, key) => {
                  acc[key] = sortObject(obj[key]);
                  return acc;
                }, {});
            }
            return obj;
          };
          const deterministicString = JSON.stringify(sortObject(response.data));
          const key = CryptoJS.enc.Base64.parse(appKey);
          const computed = CryptoJS.HmacSHA256(deterministicString, key);
          const computedBase64 = CryptoJS.enc.Base64.stringify(computed);
          if (computedBase64 !== integrityHeader) {
            console.error('Response integrity verification failed for:', response.config.url);
            console.error('Expected:', integrityHeader);
            console.error('Computed:', computedBase64);
            return Promise.reject(new Error('Response integrity verification failed'));
          } else if (config.api.enableLogging) {
            console.log('✅ Response integrity verified for:', response.config.url);
          }
        }
      } catch (e) {
        console.error('Error during integrity verification:', e);
        // Don't reject on verification errors if key is not properly configured
        if (process.env.REACT_APP_RESPONSE_INTEGRITY_KEY &&
          !process.env.REACT_APP_RESPONSE_INTEGRITY_KEY.includes('YOUR_BACKEND_APP_KEY_HERE')) {
          return Promise.reject(e);
        }
      }
    }

    return response;
  },
  (error) => {
    // Calculate request duration if metadata exists
    const duration = error.config?.metadata?.startTime
      ? new Date() - error.config.metadata.startTime
      : 0;

    // Log error in development only, and sanitize sensitive information
    if (config.api.enableLogging && process.env.NODE_ENV === 'development') {
      // Only log sanitized error information
      const sanitizedData = error.response?.data ? {
        error: error.response.data.error,
        message: error.response.data.message,
        // Don't log full error objects that might contain stack traces
      } : null;

      console.error('API Error:', {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: error.response?.status,
        duration: duration ? `${duration}ms` : 'N/A',
        message: error.message,
        data: sanitizedData,
      });
    } else if (process.env.NODE_ENV === 'production') {
      // In production, only log minimal information
      console.error('API Error:', {
        status: error.response?.status,
        url: error.config?.url,
      });
    }

    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - Clear token and redirect to login
          authStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');

          // Only redirect if not already on login page
          if (!window.location.pathname.includes('login')) {
            window.location.href = '/';
          }
          break;

        case 403:
          // Forbidden - User doesn't have permission or user is barred
          console.error('Access forbidden:', data?.message);

          // Exclude OTP limit errors from barred user redirect
          const message = data?.message || '';
          const isOtpLimitError = message.toLowerCase().includes('max') &&
            (message.toLowerCase().includes('otp') ||
              message.toLowerCase().includes('limit')) ||
            message.toLowerCase().includes('rate limit');

          // Check if user is barred based on response message (but not OTP limit)
          if (message && !isOtpLimitError && (
            message.includes('barred') ||
            message.includes('blocked') ||
            message.includes('suspended') ||
            message.includes('account has been')
          )) {
            // Clear user data and redirect to appropriate login page
            authStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            localStorage.removeItem('vigilance_user_data');

            // Redirect to barred user page
            window.location.href = '/barred-user';
          }
          break;

        case 404:
          // Not found
          console.error('Resource not found:', data?.message);
          break;

        case 422:
          // Validation error - only log in development
          if (process.env.NODE_ENV === 'development') {
            console.error('Validation error:', data?.errors);
          }
          break;

        case 429:
          // Too many requests
          console.error('Rate limit exceeded');
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors - don't log sensitive details
          if (process.env.NODE_ENV === 'development') {
            console.error('Server error:', data?.message);
          } else {
            console.error('Server error occurred');
          }
          break;

        default:
          // Don't log sensitive error details in production
          if (process.env.NODE_ENV === 'development') {
            console.error('API error:', data?.message);
          } else {
            console.error('API error occurred');
          }
      }
    } else if (isTimeoutError(error)) {
      // Aborted client-side after exceeding config.api.timeout - the server never replied in time
      console.error(`Request to ${error.config?.url} timed out after ${config.api.timeout}ms - server did not respond`);
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server - check network connection');
    } else {
      // Error in request setup
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;

